import { z } from 'zod'

import type {
  AssignedCasDetailResponse,
  AssignedCasStudentScoringItemResponse,
  AssignedCasWithHomeworkResponse,
  CasAssignmentResponse,
  CasDetailResponse,
  CasResponse,
  CasSubmitResponse,
  CreverseEntranceCasDetailResponse,
  CreverseEntranceCasResponse,
  GetScoring1Data,
  GetScoringData,
  SliceAssignedCasItemResponse,
  SliceCasSummaryResponse,
  SliceCreverseEntranceCasSummaryResponse,
  SliceCreverseGuestEntranceCasItemResponse,
  SliceGroupAssignedCasItemResponse,
  SliceGroupAssignedCasScoringResponse,
} from '~/api-spec/__generated__/main'
import { parseApiResponse } from '~/schema/apiBoundary'
import { type Slice, sliceSchema } from '~/schema/slice'

import {
  type Cas,
  type CasAssignedStudent,
  CasAssignedStudentSchema,
  type CasAssignmentDetail,
  CasAssignmentDetailSchema,
  type CasDetail,
  CasDetailSchema,
  type CasHomeworkAssignment,
  CasHomeworkAssignmentSchema,
  CasSchema,
  type CasScoringItem,
  CasScoringItemSchema,
  type CasSolvedProblem,
  CasSolvedProblemSchema,
  type CasStudentScoring,
  CasStudentScoringSchema,
  type CasSubmitResult,
  CasSubmitResultSchema,
  type EntranceCas,
  EntranceCasSchema,
  type GroupAssignedCas,
  GroupAssignedCasSchema,
  type GuestEntranceCas,
  GuestEntranceCasSchema,
  type StudentAssignedCas,
  StudentAssignedCasSchema,
} from './schemas'

const ENTITY = 'Cas'

const CasRefSchema = z.object({ casId: CasSchema.shape.casId })
const CasAssignedIdsSchema = z.object({
  assignedIds: z.array(CasAssignedStudentSchema.shape.assignedId),
})

// 생성 타입은 채점 항목의 problem 을 이름이 같은 ProblemResponse 들의 잡탕으로 그린다(학습지 필터 추천·
// 그룹 성취도 응답이 같은 칸을 덮어쓴다). 실제로는 문제 본체(problem/controller/response/ProblemResponse)가
// 온다. 2026-10-07 백엔드 develop 의 cas/controller/response/AssignedCasScoringItemResponse.kt 로 확인.
const asWireScoring = (raw: unknown): unknown => raw

export const adaptCases = (raw: SliceCasSummaryResponse): Slice<Cas> =>
  parseApiResponse(sliceSchema(CasSchema), raw, ENTITY)

export const adaptCasRef = (raw: CasResponse | CreverseEntranceCasResponse): Cas['casId'] =>
  parseApiResponse(CasRefSchema, raw, ENTITY).casId

export const adaptCasAssignedIds = (
  raw: CasAssignmentResponse,
): CasAssignedStudent['assignedId'][] =>
  parseApiResponse(CasAssignedIdsSchema, raw, ENTITY).assignedIds

export const adaptCasDetail = (
  raw: CasDetailResponse | CreverseEntranceCasDetailResponse,
): CasDetail => parseApiResponse(CasDetailSchema, raw, ENTITY)

export const adaptGroupAssignedCases = (
  raw: SliceGroupAssignedCasItemResponse,
): Slice<GroupAssignedCas> => parseApiResponse(sliceSchema(GroupAssignedCasSchema), raw, ENTITY)

export const adaptStudentAssignedCases = (
  raw: SliceAssignedCasItemResponse,
): Slice<StudentAssignedCas> => parseApiResponse(sliceSchema(StudentAssignedCasSchema), raw, ENTITY)

export const adaptCasScoringItems = (raw: GetScoring1Data | GetScoringData): CasScoringItem[] =>
  parseApiResponse(z.array(CasScoringItemSchema), asWireScoring(raw), ENTITY)

export const adaptCasStudentScorings = (
  raw: SliceGroupAssignedCasScoringResponse,
): Slice<CasStudentScoring> =>
  parseApiResponse(sliceSchema(CasStudentScoringSchema), asWireScoring(raw), ENTITY)

export const adaptEntranceCases = (
  raw: SliceCreverseEntranceCasSummaryResponse,
): Slice<EntranceCas> => parseApiResponse(sliceSchema(EntranceCasSchema), raw, ENTITY)

export const adaptGuestEntranceCases = (
  raw: SliceCreverseGuestEntranceCasItemResponse,
): Slice<GuestEntranceCas> => parseApiResponse(sliceSchema(GuestEntranceCasSchema), raw, ENTITY)

export const adaptCasHomeworkAssignments = (
  raw: AssignedCasWithHomeworkResponse[],
): CasHomeworkAssignment[] => parseApiResponse(z.array(CasHomeworkAssignmentSchema), raw, ENTITY)

export const adaptCasAssignmentDetail = (raw: AssignedCasDetailResponse): CasAssignmentDetail =>
  parseApiResponse(CasAssignmentDetailSchema, raw, ENTITY)

export const adaptCasSolvedProblems = (
  raw: AssignedCasStudentScoringItemResponse[],
): CasSolvedProblem[] => parseApiResponse(z.array(CasSolvedProblemSchema), raw, ENTITY)

export const adaptCasSubmitResult = (raw: CasSubmitResponse): CasSubmitResult =>
  parseApiResponse(CasSubmitResultSchema, raw, ENTITY)
